// pages/ProjectTemplateDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  GripVertical,
  CheckSquare,
} from "lucide-react";
import apiService from "../../services/api";
import CreateTaskTemplateModal from "../../components/CreateTaskTemplateModal";
import { useTheme } from "../../contexts/ThemeContext";

const ProjectTemplateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [template, setTemplate] = useState(null);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  useEffect(() => {
    fetchTemplate();
    fetchTaskTemplates();
  }, [id]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProjectTemplate(id);
      setTemplate(response.data);
    } catch (error) {
      console.error("Error fetching template:", error);
      navigate("/project-templates");
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskTemplates = async () => {
    try {
      const response = await apiService.getTaskTemplates(id);
      setTaskTemplates(response.data);
    } catch (error) {
      console.error("Error fetching task templates:", error);
    }
  };

  const handleDeleteTaskTemplate = async (taskTemplateId) => {
    if (window.confirm("Are you sure you want to delete this task template?")) {
      try {
        await apiService.deleteTaskTemplate(id, taskTemplateId);
        fetchTaskTemplates();
      } catch (error) {
        console.error("Error deleting task template:", error);
        alert("Failed to delete task template");
      }
    }
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Loading...
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Template not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "border-gray-700 bg-[#13161B]" : "border-gray-200 bg-white"}`}
      >
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ArrowLeft size={20} />
              <span>Back to Templates</span>
            </button>
          </div>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1
                className={`text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {template.projectTemplateName}
              </h1>
              {template.templateDescription && (
                <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                  {template.templateDescription}
                </p>
              )}
              <div
                className={`flex items-center gap-4 mt-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {template.owner && <span>Owner: {template.owner.name}</span>}
                {template.defaultManager && (
                  <>
                    <span>•</span>
                    <span>Default Manager: {template.defaultManager.name}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        {/* Task Templates Section */}
        <div
          className={`rounded-lg border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`px-6 py-4 border-b flex items-center justify-between ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div>
              <h2
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Task Templates ({taskTemplates.length})
              </h2>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Define tasks that will be created with projects using this
                template
              </p>
            </div>
            <button
              onClick={() => setShowCreateTaskModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
            >
              <Plus size={16} />
              Add Task Template
            </button>
          </div>

          <div className="p-6">
            {taskTemplates.length === 0 ? (
              <div className="text-center py-12">
                <CheckSquare
                  size={48}
                  className={`mx-auto mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
                />
                <p
                  className={`mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  No task templates yet
                </p>
                <button
                  onClick={() => setShowCreateTaskModal(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
                >
                  Add First Task Template
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {taskTemplates.map((taskTemplate, index) => (
                  <TaskTemplateRow
                    key={taskTemplate.id}
                    taskTemplate={taskTemplate}
                    index={index}
                    onDelete={handleDeleteTaskTemplate}
                    isDarkMode={isDarkMode}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Task Template Modal */}
      {showCreateTaskModal && (
        <CreateTaskTemplateModal
          projectTemplateId={id}
          onClose={() => setShowCreateTaskModal(false)}
          onSuccess={() => {
            setShowCreateTaskModal(false);
            fetchTaskTemplates();
          }}
        />
      )}
    </div>
  );
};

const TaskTemplateRow = ({ taskTemplate, index, onDelete, isDarkMode }) => {
  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
    >
      <div
        className={`cursor-move ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
      >
        <GripVertical size={20} />
      </div>

      <div className="flex-1">
        <div className="flex items-center gap-3 mb-1">
          <span
            className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            #{index + 1}
          </span>
          <span
            className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {taskTemplate.taskTemplateName}
          </span>
          <span
            className={`text-xs px-2 py-0.5 rounded ${
              taskTemplate.defaultPriority === "Urgent"
                ? "bg-red-500/20 text-red-400"
                : taskTemplate.defaultPriority === "High"
                  ? "bg-orange-500/20 text-orange-400"
                  : taskTemplate.defaultPriority === "Medium"
                    ? "bg-yellow-500/20 text-yellow-400"
                    : "bg-blue-500/20 text-blue-400"
            }`}
          >
            {taskTemplate.defaultPriority}
          </span>
        </div>

        {taskTemplate.templateDescription && (
          <p
            className={`text-sm line-clamp-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            {taskTemplate.templateDescription}
          </p>
        )}

        <div
          className={`flex items-center gap-4 mt-2 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
        >
          {taskTemplate.defaultAssignee && (
            <span>Assigned to: {taskTemplate.defaultAssignee.name}</span>
          )}
          {taskTemplate.defaultDueDate && (
            <>
              <span>•</span>
              <span>Due: +{taskTemplate.defaultDueDate} days</span>
            </>
          )}
        </div>
      </div>

      <button
        onClick={() => onDelete(taskTemplate.id)}
        className={`p-2 rounded transition-colors ${isDarkMode ? "text-gray-400 hover:text-red-400 hover:bg-red-500/10" : "text-gray-500 hover:text-red-500 hover:bg-red-50"}`}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

export default ProjectTemplateDetail;
